import _ from 'underscore';

// @ts-expect-error
import {ActivitiesCircleIcon} from 'shells/icon/svg/activities-circle-icon';
// @ts-expect-error - imported from .js/.jsx file
import {GoalActivityLoggedIcon} from 'shells/icon/svg/goal-activity-logged-icon';
// @ts-expect-error - imported from .js/.jsx file
import {GoalClickIcon} from 'shells/icon/svg/goal-click-icon';
// @ts-expect-error - imported from .js/.jsx file
import {GoalEmailIcon} from 'shells/icon/svg/goal-email-icon';
// @ts-expect-error - imported from .js/.jsx file
import {GoalInvoiceIcon} from 'shells/icon/svg/goal-invoice-icon';
// @ts-expect-error - imported from .js/.jsx file
import {GoalLeadFieldsIcon} from 'shells/icon/svg/goal-lead-fields-icon';
// @ts-expect-error - imported from .js/.jsx file
import {GoalLeadInfoIcon} from 'shells/icon/svg/goal-lead-info-icon';
// @ts-expect-error - imported from .js/.jsx file
import {GoalLeadValueIcon} from 'shells/icon/svg/goal-lead-value-icon';
// @ts-expect-error - imported from .js/.jsx file
import {GoalQuoteIcon} from 'shells/icon/svg/goal-quote-icon';

import type {GoalType} from 'nutshell-graphql-types';

import type {SalesAutomationItemType} from '../../sales-automation/types';

import {HelpClosedLeadCompanyTypes} from './onboarding/help-closed-lead-company-types/help-closed-lead-company-types';
import {HelpClosedLeadEmailMarketing} from './onboarding/help-closed-lead-email-marketing/help-closed-lead-email-marketing';
import {HelpDripSequences} from './onboarding/help-closed-lead-email-marketing/help-drip-sequences';
import {HelpClosedLeadFollowupAutomations} from './onboarding/help-closed-lead-followup-automations/help-closed-lead-followup-automations';
import {HelpClosedLeadTasks} from './onboarding/help-closed-lead-tasks/help-closed-lead-tasks';
import {HelpEmailMarketing} from './onboarding/help-email-marketing/help-email-marketing';
import {HelpNutshellEmailMarketing} from './onboarding/help-email-marketing/help-nutshell-email-marketing';
import {HelpGoals} from './onboarding/help-goals/help-goals';
import {HelpTasks} from './onboarding/help-tasks/help-tasks';
import {HelpUserAssignment} from './onboarding/help-user-assignment/help-user-assignment';
import type {Integration} from './types';

export const SLACK = 'closedLeadSlackMessage';
export const STEP_TEMPLATES = 'stepTemplates';
export const MAILCHIMP_EMAILS = 'emails';
export const CONSTANT_CONTACT_EMAILS = 'constantContactEmails';
export const NUTSHELL_EMAIL_MARKETING = 'nutshellEmailMarketing';
export const EMAIL_MARKETING_CTA = 'emailMarketingCta';
export const GOALS = 'goals';
export const TEXT_AUTOMATIONS = 'textAutomations';
export const STAGES = 'stages';
export const OPEN_LEAD_SLACK = 'slack';
export const CLOSED_LEADS_TASK_TEMPLATES = 'closedLeadTaskTemplates';
export const CLOSED_LEAD_ACCOUNT_TYPE_AUTOMATION = 'closedLeadAccountTypeAutomations';
export const CLOSED_LEAD_DRIP_SEQUENCE_START_TRIGGERS = 'closedLeadDripSequenceStartTriggers';
export const CLOSED_LEAD_FOLLOWUP_RULES = 'closedLeadFollowupRule';

type SalesAutomationConstantsMap = {
    headerHelpText: string;
    headerSubheaderText: string;
    helpComponent?: React.ComponentType<any>;
    // For a component not controlled by local state, such as an OpenableModal
    uncontrolledHelpComponent?: React.ComponentType<any>;
    isNew?: boolean;
};

const salesAutomationConstants: Partial<
    Record<SalesAutomationItemType, SalesAutomationConstantsMap>
> = {
    [CLOSED_LEAD_ACCOUNT_TYPE_AUTOMATION]: {
        headerHelpText: 'Company type',
        headerSubheaderText: 'Automatically change the related company type when a lead is closed',
        helpComponent: HelpClosedLeadCompanyTypes,
    },
    [STEP_TEMPLATES]: {
        headerHelpText: 'Tasks',
        headerSubheaderText: 'Create tasks for your team to meet each stage’s goal',
        helpComponent: HelpTasks,
    },
    [MAILCHIMP_EMAILS]: {
        headerHelpText: 'MailChimp',
        headerSubheaderText: 'Send automated emails to leads, based on stage',
        helpComponent: HelpEmailMarketing,
    },
    [CONSTANT_CONTACT_EMAILS]: {
        headerHelpText: 'Constant Contact',
        headerSubheaderText: 'Send automated emails to leads, based on stage',
        helpComponent: HelpEmailMarketing,
    },
    [NUTSHELL_EMAIL_MARKETING]: {
        headerHelpText: 'Personal email sequences',
        headerSubheaderText:
            'Send a personal email template from the lead owner when a lead enters a stage',
        helpComponent: HelpNutshellEmailMarketing,
    },
    [EMAIL_MARKETING_CTA]: {
        headerHelpText: 'Email marketing',
        headerSubheaderText: 'Send automated emails to leads, based on stage',
        helpComponent: HelpEmailMarketing,
    },
    [GOALS]: {
        headerHelpText: 'Stage goals',
        headerSubheaderText: 'Automatically move leads to the next stage after these goals are met',
        helpComponent: HelpGoals,
    },
    userAssignments: {
        headerHelpText: 'User assignments',
        headerSubheaderText: 'Control how leads are assigned to users or teams',
        helpComponent: HelpUserAssignment,
    },
    [CLOSED_LEADS_TASK_TEMPLATES]: {
        headerHelpText: 'Tasks',
        headerSubheaderText: 'Create tasks for your team when a lead is closed',
        helpComponent: HelpClosedLeadTasks,
    },
    closedLeadMailChimpSubscriptions: {
        headerHelpText: 'MailChimp',
        headerSubheaderText: 'Add your leads to an automated email campaign when they are closed',
        helpComponent: HelpClosedLeadEmailMarketing,
    },
    closedLeadConstantContactSubscriptions: {
        headerHelpText: 'Constant Contact',
        headerSubheaderText: 'Add your leads to an automated email campaign when they are closed',
        helpComponent: HelpClosedLeadEmailMarketing,
    },
    closedLeadDripSequenceStartTriggers: {
        headerHelpText: 'Drip sequences',
        headerSubheaderText: 'Send a Nutshell Campaigns drip sequence when a lead enters a stage',
        uncontrolledHelpComponent: HelpDripSequences,
    },
    [SLACK]: {
        headerHelpText: 'Slack',
        headerSubheaderText: 'Post to Slack when leads are closed',
        helpComponent: undefined,
    },
    [OPEN_LEAD_SLACK]: {
        headerHelpText: 'Slack',
        headerSubheaderText: 'Post to Slack when a lead is created or enters a new stage',
        helpComponent: undefined,
    },
    [TEXT_AUTOMATIONS]: {
        headerHelpText: 'Text automations',
        headerSubheaderText: 'Send a text message when a lead enters a stage',
        helpComponent: undefined,
    },
    [CLOSED_LEAD_FOLLOWUP_RULES]: {
        headerHelpText: 'Follow-up lead automations',
        headerSubheaderText: 'Create new leads from closed deals',
        helpComponent: HelpClosedLeadFollowupAutomations,
        isNew: true,
    },
};

export function getHeaderTitleForType(itemType: SalesAutomationItemType): string {
    // @ts-expect-error
    return salesAutomationConstants[itemType].headerHelpText;
}

export function getHeaderSubtitleForType(itemType: SalesAutomationItemType): string {
    // @ts-expect-error
    return salesAutomationConstants[itemType].headerSubheaderText;
}

export function getHelpComponent(itemType: SalesAutomationItemType) {
    // @ts-expect-error
    return salesAutomationConstants[itemType].helpComponent;
}

export function getUncontrolledHelpComponent(itemType: SalesAutomationItemType) {
    // @ts-expect-error
    return salesAutomationConstants[itemType].uncontrolledHelpComponent;
}

export function getIsNewForType(itemType: SalesAutomationItemType) {
    // @ts-expect-error
    return salesAutomationConstants[itemType].isNew;
}

type GoalIcon =
    | GoalActivityLoggedIcon
    | ActivitiesCircleIcon
    | GoalLeadFieldsIcon
    | GoalLeadInfoIcon
    | GoalLeadValueIcon
    | GoalQuoteIcon
    | GoalInvoiceIcon;

type GoalConstantsMap = {
    title: string;
    subtitle: string;
    icon: GoalIcon;
};

const goalConstants: Partial<Record<GoalType, GoalConstantsMap>> = {
    BASIC_INFO: {
        title: 'Figure out a lead’s contact information',
        subtitle: 'Gather information like an email address or phone number',
        icon: GoalLeadInfoIcon,
    },
    LEAD_VALUE: {
        title: 'Determine how much a lead is worth',
        subtitle: 'Identify if this lead is large enough to move to the next stage',
        icon: GoalLeadValueIcon,
    },
    ACTIVITY_LOGGED: {
        title: 'Complete an activity',
        subtitle: 'Log a phone call, meeting or an activity unique to your business',
        icon: GoalActivityLoggedIcon,
    },
    LEAD_FIELDS: {
        title: 'Learn something specific about the lead',
        subtitle:
            'Collect a field unique to your business like a budget, delivery details, or a project timeframe.',
        icon: GoalLeadFieldsIcon,
    },
    EMAIL_RECEIVED: {
        title: 'Receive an email from the lead',
        subtitle:
            'Wait for a lead to reply to your initial outreach. When they email you, the lead will move to the next stage.',
        icon: GoalEmailIcon,
    },
    EMAIL_SENT: {
        title: 'Send an email to the lead',
        subtitle:
            'Make initial contact with this lead. Once you’ve sent them an email, the lead will move to the next stage.',
        icon: GoalEmailIcon,
    },
    EMAIL_CLICKED: {
        title: 'Lead clicks a link in an email I send',
        subtitle:
            'Track when a lead clicks a link in your initial outreach. When they do, the lead will move to the next stage.',
        icon: GoalClickIcon,
    },
    ACTIVITY_SCHEDULED: {
        title: 'Schedule an activity',
        subtitle: 'Schedule a phone call, meeting or an activity unique to your business',
        icon: ActivitiesCircleIcon,
    },
    QUOTE_SENT: {
        title: 'Send a quote to the lead',
        subtitle: 'When the quote is sent, the lead will move to the next stage.',
        icon: GoalQuoteIcon,
    },
    QUOTE_ACCEPTED: {
        title: 'Accept a quote from the lead',
        subtitle: 'When they accept the quote, the lead will move to the next stage.',
        icon: GoalQuoteIcon,
    },
    INVOICE_SENT: {
        title: 'Send an invoice to the lead',
        subtitle: 'When the invoice is sent, the lead will move to the next stage.',
        icon: GoalInvoiceIcon,
    },
    INVOICE_PAID: {
        title: 'Mark an invoice as paid',
        subtitle:
            'When the invoice is paid and marked as such, the lead will move to the next stage.',
        icon: GoalInvoiceIcon,
    },
};

export function getGoalFormTitleForType(goalType: GoalType): string {
    // @ts-expect-error
    return goalConstants[goalType].title;
}

export function getGoalFormSubtitleForType(goalType: GoalType): string {
    // @ts-expect-error
    return goalConstants[goalType].subtitle;
}

export function getGoalIconForType(goalType: GoalType): GoalIcon {
    // @ts-expect-error
    return goalConstants[goalType].icon;
}

/*
Returns an ordered list of integration components, grouped by whether they
are set up on the account or not.

This starts with an array of objects, then uses underscore's `sortBy` method.
This is useful because [it is stable][sort-by]. This means that components
with the same status stay in the same order, realitivly. So, for our first
two integrations, Slack and MailChimp, if both are active or inactive,
MailChimp will always appear on top. However, as we add more integrations,
they will continue to sort correctly.

This also allow us to choose not to display rows, for feature flagging or
for removing rows.

[sort-by]:https://underscorejs.org/#sortBy
 */
export function groupIntegrationsByActiveStatus(unorderedIntegrations: Integration[]) {
    let integrations = unorderedIntegrations;

    // Filter out integrations that shouldn't be displayed
    integrations = integrations.filter((integration) => integration.display);
    // Sort, to group by status
    integrations = _.sortBy(integrations, (integration) => !integration.isActive);

    // Return the list of components
    return integrations.map((integration) => integration.component);
}
